// apps/client/src/pages/pipeline-tracker/tabs/OperationsOverviewTab.tsx
//
// Operations > Overview (V6 design, first and default Operations view).
//
// Ten sections, in the mockup's order. Four are live from the exports VALUTTEN already holds: the
// tasks tile, the compliance register, the compliance watchlist and broker efficiency. Two tiles
// are tenant-entered each month (Google reviews and rating, SFG audit %). Everything else waits on
// an input nobody has supplied yet and shows NO figure (founder decision, 28 Sep 2026): a proposed
// number on this page would be read as a measurement of Evoke's business. Those sections are not
// drawn as cards; they are named once, in the "Coming when the data is available" list at the foot
// of the view, with what each needs and who owes it (founder, 1 Oct 2026).
//
// Added after the mockup: "Deadlines pushed out" (DeadlineShiftSection.tsx), placed after the
// compliance watchlist. It compares consecutive weekly uploads, so it has its own read.
//
// Revenue and clawbacks are commission data and live in the main VALUTTEN section (Reports >
// Clawbacks); this view does not link to them (founder, 5 Oct 2026).
//
// The view is grouped into six numbered TrackerSection groups (This month, Compliance, Dates
// moving, Brokers, Analysts, Days in queue), as in the approved SalesTrekker redesign; Analysts was
// added once Evoke named the deal labels that mark an analyst (Matt Spears, 7 Oct 2026),
// AnalystViewSection.tsx, and Days in queue (QueueSection.tsx) for the brief's DIQ by analyst and
// by bank (20 Oct 2026). Every panel is a house panel with an accent (trackerKit) and the four
// month tiles are house stat cards sitting directly under their group heading, as on File quality,
// the reference page (founder, 5 Oct 2026).

import React, { useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowRight, CalendarClock, ChevronDown, ChevronRight, ClipboardList, ListChecks, MessageSquareText, RefreshCw,
  ShieldAlert, ShieldCheck, Star, Users,
} from 'lucide-react';
import { SkeletonStatCards, SkeletonTable } from '../../../components/common/SkeletonLoader';
import {
  useOperationsOverview,
  type BrokerEfficiencyData,
  type ComplianceGroupStatus,
  type ComplianceItem,
  type ComplianceRegisterData,
  type ComplianceWatchGroup,
  type MonthlyInputGrading,
  type MonthlyInputs,
  type OperationsOverviewResponse,
} from '../../../hooks/pipelineTracker/useOperationsOverview';
import { monthlyInputGrade } from '../../../hooks/pipelineTracker/targetsContract';
import {
  OWNER_CHRISTIAN,
  OWNER_EVOKE,
  OWNER_SALESTREKKER,
} from '../../../hooks/pipelineTracker/operationsContract';
import { ComingSoonList, Honesty, Pill, num, type ComingSoonItem } from './pipelineParts';
import {
  DaysChip,
  EntryHistory,
  GlanceTile,
  type GlanceGrade,
  GroupPill,
  ROW_HOVER,
  TonePanel,
  groupTone,
  ProgressBar,
  StarRating,
  glanceToneColour,
  monthName,
  monthOnMonth,
  prettyDate,
  previousMonth,
  shortRange,
} from './operationsParts';
import EfficiencyChart from './EfficiencyChart';
import { efficiencyChartRows } from './efficiencyChartData';
import DeadlineShiftSection from './DeadlineShiftSection';
import AnalystViewSection from './AnalystViewSection';
import QueueSection from './QueueSection';
import { SECTION_COLOUR, TrackerSection } from './TrackerSection';
import { Chip, HouseStatGrid, chartColour } from './trackerKit';

export interface OperationsOverviewTabProps {
  companyId: string;
  /** Switches Operations to its Tasks view. The tasks tile's "Open Tasks" link calls it. */
  onOpenTasks?: () => void;
  /** super_admin or administrator: may name the labels that mark an analyst. */
  canEdit?: boolean;
}

type OkResponse = Extract<OperationsOverviewResponse, { status: 'ok' }>;

/**
 * The Operational overview's metrics that have no data source yet. Not drawn as tiles; listed in
 * the view's "Coming when the data is available" list. Kept as data so the test can assert every
 * one is listed and none renders as a figure.
 */
export const OPERATIONAL_WAITING_LINES: ReadonlyArray<ComingSoonItem> = [
  {
    section: 'DIP SLA',
    from: OWNER_SALESTREKKER,
    needs: 'Request and return timestamps on every deal type. Status dates exist only on residential deals lodged through ApplyOnline.',
  },
  {
    section: 'MIR SLA',
    from: OWNER_SALESTREKKER,
    needs: 'Request and return timestamps on every deal type.',
  },
  {
    section: 'First time right',
    from: OWNER_SALESTREKKER,
    needs: 'Request and return timestamps on every deal type, to tell a first return from a resubmission.',
  },
  {
    section: 'Compliance %',
    from: OWNER_EVOKE,
    needs: 'Pass thresholds for file quality. Operations > File quality applies no pass or fail until they are set.',
  },
  {
    section: 'Paused deals overdue',
    from: OWNER_SALESTREKKER,
    needs: 'A paused-since date on each deal. No export carries one today.',
  },
];

/**
 * Every section of the Overview that cannot be shown yet, in the order the mockup places them.
 * Rendered by ComingSoonList at the foot of the view; none of them is drawn as a card.
 */
export const OPERATIONS_COMING_SOON: ReadonlyArray<ComingSoonItem> = [
  {
    section: 'Team capacity, ranked',
    from: OWNER_CHRISTIAN,
    needs: 'The capacity measure to use for each broker (how workload and capacity are defined).',
  },
  ...OPERATIONAL_WAITING_LINES,
  {
    section: 'Multiple MIR distribution by broker',
    from: OWNER_SALESTREKKER,
    needs: 'A count of bank MIR requests per deal.',
  },
  {
    section: 'Multiple MIRs register',
    from: OWNER_SALESTREKKER,
    needs: 'A count of bank MIR requests per deal.',
  },
  {
    section: 'Meetings by month (broker efficiency)',
    from: OWNER_SALESTREKKER,
    needs: 'A date on the meeting stage. The export does not date meetings.',
  },
];

/** Where the four tiles come from. Said once, in the group heading, not again in the panel. */
export const OPERATIONAL_OVERVIEW_SOURCES =
  'Tasks are live from the Tasks export. Google reviews and the SFG audit result are entered by the practice each month.';

/** The month the monthly inputs are read for: the Tasks snapshot's, else the Performance one's. */
function inputsMonth(report: OkResponse): string | null {
  if (report.tasks.status === 'ok') return report.tasks.asAtDate.slice(0, 7);
  if (report.efficiency.status === 'ok') return report.efficiency.asAtDate.slice(0, 7);
  return null;
}

/** The tasks tile's link to Operations > Tasks, where the per-person figures behind it live. */
const OpenTasksLink: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex shrink-0 items-center gap-1 text-[0.6875rem] font-semibold text-blue-300 hover:text-blue-200"
  >
    Open Tasks
    <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
  </button>
);

/**
 * One entered tile's grade, or null for no dot: no target set, no figure entered, or the targets
 * could not be read. `shown` prints the target in the tile's own format.
 */
function inputGrade(
  actual: number | undefined,
  key: 'googleReviewCount' | 'googleRating' | 'sfgAuditPct',
  grading: MonthlyInputGrading | null,
  shown: (target: number) => string,
): GlanceGrade | null {
  if (!grading) return null;
  const target = grading.targets[key];
  const grade = monthlyInputGrade(actual, target, grading.rag);
  if (!grade || target === undefined) return null;
  return { tone: grade.tone, text: `Target ${shown(target)} \u00b7 ${grade.pct}%` };
}

const auditFigure = (pct: number): string => `${Math.round(pct * 10) / 10}%`;

/** How many months of entries the reviews tile draws, the inputs month last. */
const REVIEW_HISTORY_MONTHS = 6;

/**
 * The four headline tiles, one component and one layout for all of them (founder, 5 Oct 2026: the
 * tasks tile carried a paragraph while the three entered tiles stood half empty). Each has a value,
 * a small visual and a one-line footer; the tasks tile's full sentence is its hover title and
 * screen-reader text, so nothing it said before is lost. A month-on-month line is drawn only
 * against a figure actually entered for the previous month.
 */
const OperationalOverview: React.FC<{
  report: OkResponse;
  monthlyInputs: MonthlyInputs;
  monthlyInputsError: string | null;
  inputGrading?: MonthlyInputGrading | null;
  inputTargetsError?: string | null;
  onOpenTasks?: () => void;
}> = ({ report, monthlyInputs, monthlyInputsError, inputGrading = null, inputTargetsError = null, onOpenTasks }) => {
  const month = inputsMonth(report);
  const entry = month ? monthlyInputs[month] : undefined;
  const prevMonth = month ? previousMonth(month) : null;
  const prev = prevMonth ? monthlyInputs[prevMonth] : undefined;
  const monthLabel = month ? monthName(month) : 'this month';
  const notEntered = monthlyInputsError
    ? 'The monthly inputs could not be read'
    : `Not entered for ${monthLabel}`;
  const compare = (current: number, previous: number | undefined, options?: { decimals?: number; unit?: string }) =>
    prevMonth ? monthOnMonth(current, previous, prevMonth, options) : `Entered for ${monthLabel}`;

  const rating = entry?.googleRating;
  const reviews = entry?.googleReviewCount;
  const audit = entry?.sfgAuditPct;
  const reviewHistory = month
    ? Array.from({ length: REVIEW_HISTORY_MONTHS }, (_, i) => REVIEW_HISTORY_MONTHS - 1 - i)
      .map((back) => {
        let ym = month;
        for (let k = 0; k < back; k += 1) ym = previousMonth(ym);
        return { ym, value: monthlyInputs[ym]?.googleReviewCount };
      })
    : [];
  const tasks = report.tasks.status === 'ok' ? report.tasks : null;
  const openTasks = onOpenTasks ? <OpenTasksLink onClick={onOpenTasks} /> : undefined;
  // Nothing entered for the month: one tile says so across the three slots, rather than three
  // tiles each reading "Not entered" (founder, 6 Oct 2026). Any one entered keeps all three.
  const noneEntered = !monthlyInputsError && rating === undefined && reviews === undefined && audit === undefined;
  const ratingGrade = inputGrade(rating, 'googleRating', inputGrading, (t) => t.toFixed(1));
  const reviewsGrade = inputGrade(reviews, 'googleReviewCount', inputGrading, num);
  const auditGrade = inputGrade(audit, 'sfgAuditPct', inputGrading, auditFigure);
  // Said, not swallowed: an ungraded tile because the targets failed to load is not "no target".
  const targetsUnread = inputTargetsError !== null && !noneEntered && !monthlyInputsError;

  return (
    <>
    <HouseStatGrid count={4} testId="ops-month-tiles">
        {tasks ? (
          <GlanceTile
            label="Tasks done / outstanding"
            tone="green"
            icon={ListChecks}
            value={`${num(tasks.doneInWindow)} / ${num(tasks.outstanding)}`}
            visual={
              <ProgressBar
                fraction={tasks.dueInWindow > 0 ? tasks.doneInWindow / tasks.dueInWindow : 0}
                colour={glanceToneColour('green')}
                label={`${num(tasks.doneInWindow)} of ${num(tasks.dueInWindow)} due tasks done`}
              />
            }
            footer={`${num(tasks.overdue)} overdue · due ${shortRange(tasks.window.start, tasks.window.end)}`}
            detail={`Done: ${num(tasks.doneInWindow)} of ${num(tasks.dueInWindow)} due ${prettyDate(tasks.window.start)} to ${prettyDate(tasks.window.end)}. Outstanding as at ${prettyDate(tasks.asAtDate)}, ${num(tasks.overdue)} overdue.`}
            action={openTasks}
          />
        ) : (
          <GlanceTile
            label="Tasks done / outstanding"
            tone="green"
            icon={ListChecks}
            value={null}
            emptyValue="No export yet"
            footer="No Tasks export has been published yet."
            action={openTasks}
          />
        )}
        {noneEntered ? (
          <div className="flex sm:col-span-1 xl:col-span-3 [&>*]:flex-1" data-testid="ops-inputs-not-entered">
            <GlanceTile
              label="Monthly inputs"
              tone="brand"
              icon={ClipboardList}
              value={null}
              footer={`Google rating, Google reviews and SFG audit for ${monthLabel}: add them on Pipeline, under Targets and monthly inputs.`}
              detail={`None of the monthly inputs is entered for ${monthLabel}. Add Google rating, Google reviews and the SFG audit result on the Pipeline tab, under Targets and monthly inputs.`}
            />
          </div>
        ) : (
          <>
            <GlanceTile
              label="Google rating"
              tone="amber"
              icon={Star}
              value={rating !== undefined ? rating.toFixed(1) : null}
              visual={rating !== undefined ? <StarRating rating={rating} /> : undefined}
              footer={rating !== undefined ? compare(rating, prev?.googleRating, { decimals: 1 }) : notEntered}
              detail={rating !== undefined ? `Entered for ${monthLabel}` : undefined}
              grade={ratingGrade}
            />
            <GlanceTile
              label="Google reviews"
              tone="brand"
              icon={MessageSquareText}
              value={reviews !== undefined ? num(reviews) : null}
              visual={reviews !== undefined
                ? <EntryHistory points={reviewHistory} colour={glanceToneColour('brand')} noun="Google reviews" />
                : undefined}
              footer={reviews !== undefined ? compare(reviews, prev?.googleReviewCount) : notEntered}
              detail={reviews !== undefined ? `Entered for ${monthLabel}` : undefined}
              grade={reviewsGrade}
            />
            <GlanceTile
              label="SFG audit result"
              tone="violet"
              icon={ShieldCheck}
              value={audit !== undefined ? auditFigure(audit) : null}
              visual={audit !== undefined
                ? <ProgressBar fraction={audit / 100} colour={glanceToneColour('violet')} label={`${Math.round(audit * 10) / 10}% of 100`} />
                : undefined}
              footer={audit !== undefined ? compare(audit, prev?.sfgAuditPct, { decimals: 1, unit: 'pts' }) : notEntered}
              detail={audit !== undefined ? `Entered for ${monthLabel}` : undefined}
              grade={auditGrade}
            />
          </>
        )}
    </HouseStatGrid>
    {targetsUnread && (
      <p role="status" className="mt-2 text-xs text-amber-300">
        Targets could not be read, so these tiles are not graded right now.
      </p>
    )}
    </>
  );
};

/** The month the entered tiles are read for, as a chip in the group heading. */
const MonthChip: React.FC<{ report: OkResponse }> = ({ report }) => {
  const month = inputsMonth(report);
  return (
    <Chip tone="blue">
      <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
      Monthly inputs shown for {month ? monthName(month) : 'this month'}
    </Chip>
  );
};

const RegisterTable: React.FC<{ rows: ComplianceItem[]; daysLabel: string; overdue: boolean; stickyHead?: boolean }> = ({
  rows,
  daysLabel,
  overdue,
  stickyHead = false,
}) => (
  <table className="w-full text-sm">
    <thead className={stickyHead ? 'sticky top-0 z-10 bg-gray-900' : undefined}>
      <tr className="text-left text-[0.6875rem] uppercase tracking-wider text-gray-400">
        <th className="pb-2 pl-2 font-medium">Deal reference</th>
        <th className="pb-2 font-medium">Task group</th>
        <th className="pb-2 font-medium">Owner</th>
        <th className="pb-2 pr-2 text-right font-medium">{daysLabel}</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((row) => (
        <tr key={row.taskId} className={`border-t border-gray-700/50 odd:bg-white/[0.015] ${ROW_HOVER}`}>
          <td className="py-2 pl-2 font-mono text-xs text-gray-300">{row.dealRef ?? 'No deal reference'}</td>
          <td className="py-2">
            <GroupPill groupKey={row.groupKey}>{row.groupLabel}</GroupPill>
          </td>
          <td className="py-2 text-gray-200">{row.owner}</td>
          <td className="py-2 pr-2 text-right">
            <DaysChip days={row.days} overdue={overdue} />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

/** Which groups have no Task name mapped, stated rather than shown as zero. */
const UnmappedGroupsNote: React.FC<{ groups: ComplianceGroupStatus[] }> = ({ groups }) => {
  const unmapped = groups.filter((g) => !g.mapped);
  if (unmapped.length === 0) return null;
  return (
    <div className="mb-3 rounded-lg border border-dashed border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-sm text-amber-100">
      No Salestrekker task name is mapped to{' '}
      <b className="font-semibold">{unmapped.map((g) => g.label).join(', ')}</b> yet, so{' '}
      {unmapped.length === 1 ? 'that group shows' : 'those groups show'} no figure here. The task
      names still waiting for a group are listed on Operations &gt; Tasks, under Task names that need
      mapping.
    </div>
  );
};

/**
 * Rows the register shows before "Show all". Uncapped, a practice with dozens of overdue reviews
 * stretched the register far below the watchlist beside it and left that column mostly empty
 * (founder, 5 Oct 2026). Same pattern as Deadlines pushed out (DeadlineShiftSection's CappedTable).
 */
export const REGISTER_TOP = 6;

const ComplianceRegister: React.FC<{ data: ComplianceRegisterData }> = ({ data }) => {
  const [all, setAll] = useState(false);
  const anyMapped = data.groups.some((g) => g.mapped);
  const rows = data.register.rows;
  const capped = rows.length > REGISTER_TOP;
  const shown = all || !capped ? rows : rows.slice(0, REGISTER_TOP);
  const scrolling = all && capped;
  return (
    <TonePanel
      title="Compliance issues register"
      tone="red"
      icon={ShieldAlert}
      badge={anyMapped ? <Pill tone={data.register.total > 0 ? 'bad' : 'good'}>{num(data.register.total)} overdue</Pill> : undefined}
      blurb={`Open annual review, interest-only expiry and commercial facility expiry tasks past their due date as at ${prettyDate(data.asAtDate)}, oldest first. From the Tasks export's due dates.`}
    >
      <UnmappedGroupsNote groups={data.groups} />
      {anyMapped && data.register.total === 0 && (
        <p className="text-sm text-gray-400">Nothing in the mapped groups is overdue as at {prettyDate(data.asAtDate)}.</p>
      )}
      {rows.length > 0 && (
        <div data-testid="compliance-register">
          <div className={`overflow-x-auto ${scrolling ? 'max-h-[26rem] overflow-y-auto' : ''}`}>
            <RegisterTable rows={shown} daysLabel="Days overdue" overdue stickyHead={scrolling} />
          </div>
          {capped && (
            <div className="mt-3 border-t border-gray-700/60">
              <button
                type="button"
                onClick={() => setAll((v) => !v)}
                aria-expanded={all}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-2.5 text-left text-sm text-gray-200 hover:text-white ${ROW_HOVER}`}
              >
                {all ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                {all ? `Show oldest ${num(REGISTER_TOP)} overdue` : `Show all ${num(rows.length)} overdue`}
                <span className="ml-auto">
                  <Pill>{num(rows.length)}</Pill>
                </span>
              </button>
            </div>
          )}
          {data.register.total > rows.length && (
            <p className="mt-3 text-xs text-gray-400">
              The register lists the {num(rows.length)} oldest of {num(data.register.total)}.
            </p>
          )}
        </div>
      )}
    </TonePanel>
  );
};

const WatchGroup: React.FC<{ group: ComplianceWatchGroup; mapped: boolean }> = ({ group, mapped }) => {
  const [open, setOpen] = useState(false);
  if (!mapped) {
    return (
      <div className="flex items-center justify-between border-t border-gray-700/60 px-2 py-2.5 text-sm">
        <span className="text-gray-300">{group.label}</span>
        <span className="text-xs text-gray-500">Not mapped</span>
      </div>
    );
  }
  return (
    <div className="border-t border-gray-700/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`flex w-full items-center gap-2 rounded-md px-2 py-2.5 text-left text-sm text-gray-200 hover:text-white ${ROW_HOVER}`}
      >
        {open ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
        <span aria-hidden="true" data-group-tone={groupTone(group.key)} className="h-2 w-2 rounded-full"
          style={{ background: glanceToneColour(groupTone(group.key)) }} />
        {group.label}
        <span className="text-xs text-gray-500">within {group.watchDays} days</span>
        <span className="ml-auto">
          <Pill tone={group.total > 0 ? 'warn' : 'muted'}>{num(group.total)} total</Pill>
        </span>
      </button>
      {open && (
        <div className="pb-3 pl-8">
          {group.rows.length === 0 ? (
            <p className="text-sm text-gray-400">Nothing falls due in the next {group.watchDays} days.</p>
          ) : (
            <>
              <RegisterTable rows={group.rows} daysLabel="Days until due" overdue={false} />
              {group.total > group.rows.length && (
                <p className="mt-2 text-xs text-gray-400">
                  Showing the {group.rows.length} soonest of {num(group.total)}.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ComplianceWatchlist: React.FC<{ data: ComplianceRegisterData }> = ({ data }) => (
  // Amber, not the section's rose: "approaching due" beside the red overdue register is the
  // distinction the pair exists to draw, so this panel keeps its own colour.
  <TonePanel
    title="Compliance watchlist: approaching due"
    tone="amber"
    semantic
    icon={CalendarClock}
    blurb={`Open, not yet overdue, as at ${prettyDate(data.asAtDate)}: interest-only and facility expiries due within 180 days, annual reviews within 30. Each group opens on click. Once an item passes its due date it moves to the register.`}
  >
    <UnmappedGroupsNote groups={data.groups} />
    {data.watchlist.map((group) => (
      <WatchGroup
        key={group.key}
        group={group}
        mapped={data.groups.find((g) => g.key === group.key)?.mapped ?? false}
      />
    ))}
  </TonePanel>
);

const EFFICIENCY_CHARTS = [
  { metric: 'leads' as const, title: 'Leads created by month', accent: '#3b82f6' },
  { metric: 'submissions' as const, title: 'Submissions lodged by month', accent: '#8b5cf6' },
  { metric: 'settlements' as const, title: 'Settlements by month', accent: '#10b981' },
];

const BrokerEfficiency: React.FC<{ data: BrokerEfficiencyData }> = ({ data }) => {
  const otherTotal = useMemo(
    () => (['leads', 'submissions', 'settlements'] as const)
      .reduce((n, k) => n + data.other[k].reduce((a, b) => a + b, 0), 0),
    [data],
  );
  return (
    <TonePanel
      title="Broker efficiency"
      tone="green"
      icon={Users}
      badge={<Pill tone="good">Live</Pill>}
      blurb={`Per broker, by month, ${prettyDate(data.from)} to ${prettyDate(data.to)}: the same dated events as Deal movements on the Pipeline tab. The last month runs to the snapshot date, so it is partial. Period totals are in the Pipeline broker table, so no totals table is repeated here.`}
    >
      {data.series.length === 0 ? (
        <p className="text-sm text-gray-400">No broker has a dated event in these twelve months.</p>
      ) : (
        <>
          {/* One chart per row, full width (founder, 5 Oct 2026): side by side, a twelve-month
              line per broker was too narrow to read. */}
          <div className="grid grid-cols-1 gap-3" data-testid="efficiency-charts">
            {EFFICIENCY_CHARTS.map(({ metric, title, accent }) => (
              <EfficiencyChart
                key={metric}
                title={title}
                accent={accent}
                rows={efficiencyChartRows(data, metric)}
                series={data.series}
              />
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5 text-xs text-gray-300">
            {data.series.map((s, i) => (
              <span key={s.key} className="inline-flex items-center gap-1.5 rounded-full border border-gray-700/80 bg-gray-900/40 px-2.5 py-0.5">
                <span className="h-2 w-2 rounded-full" style={{ background: chartColour(i) }} />
                {s.name}
                {s.kind === 'group' && <span className="text-gray-500">(book)</span>}
              </span>
            ))}
          </div>
        </>
      )}
      {otherTotal > 0 && (
        <div className="mt-3">
          <Honesty>
            {num(otherTotal)} dated events belong to support staff or to deals with no owner. They are
            counted in Deal movements and not charted here as a broker.
          </Honesty>
        </div>
      )}
    </TonePanel>
  );
};

/**
 * No task name mapped to any compliance group: one line saying what turns the register on and
 * where, in place of two panels that could only ever be empty (founder, 6 Oct 2026). Once any group
 * is mapped both panels return, each naming the groups still unmapped.
 */
const ComplianceNotSetUp: React.FC<{ groups: ComplianceGroupStatus[]; onOpenTasks?: () => void }> = ({ groups, onOpenTasks }) => (
  <div
    className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-sm text-amber-100 xl:col-span-2"
    data-testid="compliance-not-set-up"
  >
    <ShieldAlert className="h-4 w-4 shrink-0 text-amber-300" aria-hidden="true" />
    <span className="min-w-0 flex-1">
      The compliance register and watchlist start once a Salestrekker task name is mapped to{' '}
      <b className="font-semibold">{groups.map((g) => g.label).join(', ')}</b>. Map them on Operations &gt; Tasks, under
      Task names that need mapping.
    </span>
    {onOpenTasks && <OpenTasksLink onClick={onOpenTasks} />}
  </div>
);

const NoTasks: React.FC<{ title: string; tone: 'red' | 'amber'; icon: typeof ShieldAlert }> = ({ title, tone, icon }) => (
  <TonePanel title={title} tone={tone} icon={icon} semantic={tone === 'amber'}>
    <p className="text-sm text-gray-400">
      No Tasks export has been published yet. The register reads the due dates on annual review,
      interest-only expiry and facility expiry tasks, so it fills in with the first Tasks upload.
    </p>
  </TonePanel>
);

/**
 * Groups whose colour is not their step number's. Compliance is rose, the overdue register's red;
 * Brokers is green, the colour Broker efficiency has always worn. "This month" (blue) and "Dates
 * moving" (violet) keep their step colours, so no two neighbouring groups share one.
 */
const OPS_ACCENT = {
  compliance: SECTION_COLOUR.rose,
  brokers: SECTION_COLOUR.green,
  // Not step 5's own green, which Brokers above already wears.
  analysts: SECTION_COLOUR.cyan,
  // Step 6's own lime sits beside Analysts' cyan; violet is the queue's and reads apart from both.
  queue: SECTION_COLOUR.violet,
};

const OperationsOverviewTab: React.FC<OperationsOverviewTabProps> = ({ companyId, onOpenTasks, canEdit = false }) => {
  const { response, loading, error, refetch, monthlyInputs, monthlyInputsError, inputGrading, inputTargetsError } =
    useOperationsOverview(companyId);
  const report = response && response.status === 'ok' ? response : null;

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-red-900/60 bg-red-950/30 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" aria-hidden="true" />
          <div className="flex-1 text-sm text-red-300">
            <p>{error}</p>
            <button
              type="button"
              onClick={refetch}
              className="mt-2 inline-flex items-center gap-2 rounded-md border border-red-800 px-3 py-1.5 text-xs text-red-200 hover:bg-red-900/40"
            >
              <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
              Try again
            </button>
          </div>
        </div>
      )}

      {response && response.status === 'invalid_request' && (
        <div className="rounded-lg border border-amber-900/60 bg-amber-950/30 p-4 text-sm text-amber-300">
          The overview could not be read: {response.reason}
        </div>
      )}

      {loading && !report && (
        <div className="space-y-4">
          <SkeletonStatCards count={4} columns={4} />
          <SkeletonTable rows={6} columns={4} />
        </div>
      )}

      {report && (
        <TrackerSection
          id="ops-month"
          step="1 · This month"
          title="This month at a glance"
          description={OPERATIONAL_OVERVIEW_SOURCES}
          actions={<MonthChip report={report} />}
        >
          <OperationalOverview
            report={report}
            monthlyInputs={monthlyInputs}
            monthlyInputsError={monthlyInputsError}
            inputGrading={inputGrading}
            inputTargetsError={inputTargetsError}
            onOpenTasks={onOpenTasks}
          />
        </TrackerSection>
      )}

      {report && (
        <TrackerSection
          id="ops-compliance"
          step="2 · Compliance"
          accent={OPS_ACCENT.compliance}
          title="Compliance register and watchlist"
          description="From the Tasks export's due dates."
        >
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            {report.compliance.status === 'ok' ? (
              <>
                {report.compliance.groups.some((g) => g.mapped) ? (
                  <>
                    <ComplianceRegister data={report.compliance} />
                    <ComplianceWatchlist data={report.compliance} />
                  </>
                ) : (
                  <ComplianceNotSetUp groups={report.compliance.groups} onOpenTasks={onOpenTasks} />
                )}
              </>
            ) : (
              <>
                <NoTasks title="Compliance issues register" tone="red" icon={ShieldAlert} />
                <NoTasks title="Compliance watchlist: approaching due" tone="amber" icon={CalendarClock} />
              </>
            )}
          </div>
        </TrackerSection>
      )}

      <TrackerSection
        id="ops-dates"
        step="3 · Dates moving"
        title="Which dates moved later between uploads"
        description="Pushes are read by comparing weekly uploads, attributed to the deal's owner or the task's assignee; a date moved and moved back within one week is not seen."
      >
        {/* Its own read and its own loading state: comparing many weekly uploads is a heavier query
            than the latest-snapshot register above, and must not hold that register back. */}
        <DeadlineShiftSection companyId={companyId} />
      </TrackerSection>

      {report && (
        <TrackerSection
          id="ops-brokers"
          step="4 · Brokers"
          accent={OPS_ACCENT.brokers}
          title="Monthly activity per broker"
          description="Per broker, by month: the same dated events as Deal movements on the Pipeline tab. The last month is partial. Period totals are in the Pipeline broker table."
        >
          {report.efficiency.status === 'ok' ? (
            <BrokerEfficiency data={report.efficiency} />
          ) : (
            <TonePanel title="Broker efficiency" tone="green" icon={Users}>
              <p className="text-sm text-gray-400">
                No Performance export has been published yet. The charts read its dated events.
              </p>
            </TonePanel>
          )}
        </TrackerSection>
      )}

      {report && (
        <TrackerSection
          id="ops-analysts"
          step="5 · Analysts"
          accent={OPS_ACCENT.analysts}
          title="What each analyst is working"
          description="From the labels your team puts on a deal to mark its credit analyst. The deal owner is still the broker, so analysts never appear in a broker table."
        >
          <AnalystViewSection companyId={companyId} data={report.analysts} canEdit={canEdit} onSaved={refetch} />
        </TrackerSection>
      )}

      {report && (
        <TrackerSection
          id="ops-queue"
          step="6 · Days in queue"
          accent={OPS_ACCENT.queue}
          title="How long deals wait with lenders"
          description="In-flight deals in the lender stages (lodged, conditional, unconditional) and how many days each has sat there, by analyst label and by lender. From the Performance export's stage-entered dates; the lender comes from the Performance export, else the Sales export."
        >
          <QueueSection data={report.queue} />
        </TrackerSection>
      )}

      {/* The list renders without the report: it depends on nothing the report returns, so a
          reader sees what is owed even while the live sections load. */}
      <ComingSoonList items={OPERATIONS_COMING_SOON} />
    </div>
  );
};

export default OperationsOverviewTab;
